import { useSearchParams } from "react-router-dom";

// Admin login. Same OAuth shape as the user app's Login: two
// <a href> redirects to ${API_BASE}/api/v1/auth/oauth/{provider}/start
// with `?return_to=` pointing back at the admin frontend. The
// is_admin gate happens server-side via the Admin middleware on
// every admin endpoint; non-admin users land on /no-access via
// RequireAuth's frontend guard.
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export function Login() {
  const [params] = useSearchParams();
  const returnToParam = params.get("returnTo");
  // 0024: orchestrator's OAuth callback redirects here with
  // `?signin_no_account=1` when the user signed in with a provider
  // account that has no record in our system. Admin doesn't have a
  // signup flow (admins are promoted, not created) -- the banner
  // explains that and tells the user to contact an existing admin.
  const signinNoAccount = params.get("signin_no_account") === "1";
  // RequireAuth sets `?returnTo=` to the attempted RELATIVE path
  // (e.g. /users/u_xxx) so the post-OAuth round-trip lands the
  // operator where they were trying to go. The orchestrator's
  // OAuthStart with `mode=signin` requires an ABSOLUTE return_to
  // (it parses the origin out for the signin-fail redirect, per
  // 0024 F8) -- so we resolve relative paths against the admin
  // frontend's origin before sending.
  const returnTo = returnToParam
    ? `${window.location.origin}${returnToParam}`
    : `${window.location.origin}/`;

  const buildOAuthUrl = (provider: "google" | "microsoft") =>
    `${API_BASE}/api/v1/auth/oauth/${provider}/start?mode=signin&return_to=${encodeURIComponent(returnTo)}`;

  return (
    <main className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-3">
          Eminity Admin
        </p>
        <h1 className="text-display font-medium text-ink-50 tracking-tightest leading-[1.05] mb-3">
          Sign in
        </h1>
        {signinNoAccount ? (
          <div
            role="status"
            className="mb-6 rounded-md border border-amber-700/40 bg-amber-950/30 px-3 py-2 text-xs text-amber-200 leading-relaxed"
          >
            We didn't find an account for that sign-in. Admin access is
            granted by an existing admin — there's no self-signup here.
            Contact an admin to be promoted, or try a different account.
          </div>
        ) : null}
        <p className="text-sm text-ink-300 mb-8 leading-relaxed">
          Operator console for ProductModelingTool. Requires an admin
          account — non-admin users will be redirected.
        </p>
        <div className="space-y-3">
          <a
            href={buildOAuthUrl("google")}
            className="block w-full text-center bg-ink-900 border border-ink-700 hover:border-accent rounded-md px-4 py-2.5 text-sm text-ink-100 transition-colors"
          >
            Continue with Google
          </a>
          <a
            href={buildOAuthUrl("microsoft")}
            className="block w-full text-center bg-ink-900 border border-ink-700 hover:border-accent rounded-md px-4 py-2.5 text-sm text-ink-100 transition-colors"
          >
            Continue with Microsoft
          </a>
        </div>
      </div>
    </main>
  );
}
